/* iter55g24/g25 — Revision Mode side panel.
 *
 * A right-anchored Sheet that appears when the writer switches the
 * top-menu mode to "Revision". Streams a Claude editorial review via
 * SSE (bypasses Cloudflare 524 timeouts on long chapters) and shows
 * BOTH:
 *   • Structured suggestions grouped by category (Grammar, Pacing,
 *     Character Voice, Other) — each with an inline "Apply" button
 *     that seeds an editable rewrite dialog.
 *   • A single freeform paragraph of editorial guidance / rewrite.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Wand2, MessageSquareText, ListChecks, PenLine, Check, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { streamSSE } from '@/lib/auth_api';
import { chaptersApi, revisionApi } from '@/lib/api';
import { usePW } from '@/lib/store';
import { cn } from '@/lib/utils';
import ApplySuggestionDialog from './ApplySuggestionDialog';

function useSelectionText(scrollAreaRef) {
  const [text, setText] = useState('');
  useEffect(() => {
    const onSelect = () => {
      try {
        const sel = window.getSelection();
        const s = sel ? sel.toString().trim() : '';
        if (s.length > 15) setText(s);
      } catch { /* ignore */ }
    };
    document.addEventListener('selectionchange', onSelect);
    return () => document.removeEventListener('selectionchange', onSelect);
  }, [scrollAreaRef]);
  return [text, setText];
}

const CATEGORY_META = [
  { key: 'grammar',        label: 'Grammar & clarity' },
  { key: 'pacing',         label: 'Pacing & rhythm' },
  { key: 'character_voice', label: 'Character voice' },
  { key: 'other',          label: 'Other notes' },
];

export default function RevisionModePanel({
  open, onClose,
  projectId, chapterId, chapterTitle,
  scrollAreaRef,
}) {
  const [selectionText, setSelectionText] = useSelectionText(scrollAreaRef);
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState('');
  const [applying, setApplying]   = useState(null);  // { note, rewrite, saving }
  // acceptedNotes — tracks suggestion strings the author has already rewritten.
  // Passed to the backend on every re-scan so Claude won't resurface them.
  const [acceptedNotes, setAcceptedNotes] = useState(new Set());
  const { setChapters } = usePW();

  const run = useCallback(async ({ useSelection = false } = {}) => {
    if (!projectId || !chapterId) return;
    setLoading(true);
    setError('');
    setResult(null);
    const body = {
      chapter_id: chapterId,
      // Send the accepted list so Claude skips already-resolved issues.
      accepted_notes: acceptedNotes.size ? [...acceptedNotes] : undefined,
    };
    if (useSelection && selectionText.trim()) {
      body.selection_text = selectionText.trim().slice(0, 20000);
    }
    let final = null;
    let gotError = '';
    try {
      await streamSSE(
        `/projects/${projectId}/stream/revision-review`,
        body,
        {
          onEvent: (ev) => {
            if (ev.event === 'done') final = ev.data;
            else if (ev.event === 'error') gotError = ev.data?.message || 'Review failed.';
          },
        },
      );
    } catch (e) {
      gotError = e?.message || 'Connection dropped.';
    }
    if (final) {
      setResult(final);
    } else {
      const msg = gotError || 'Review returned no result. Try again.';
      setError(msg);
      toast.error(msg);
    }
    setLoading(false);
  }, [projectId, chapterId, selectionText, acceptedNotes]);

  // On panel open for a new chapter: load persisted accepted notes from DB.
  useEffect(() => {
    if (!open || !chapterId) return;
    setResult(null);
    setError('');
    // Load previously persisted accepted notes for this chapter, then run.
    revisionApi.getAccepted(projectId, chapterId)
      .then((data) => setAcceptedNotes(new Set(data.notes || [])))
      .catch(() => setAcceptedNotes(new Set()))
      .finally(() => run({ useSelection: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, chapterId]);

  if (!open) return null;

  const struct = result?.structured || {};
  const hasStructured = CATEGORY_META.some((c) => (struct[c.key] || []).length > 0);
  const hasSelection = selectionText && selectionText.trim().length > 15;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent
        side="right"
        className="flex w-[420px] max-w-[95vw] flex-col overflow-hidden p-0 sm:max-w-[420px]"
        data-testid="revision-mode-panel"
      >
        <SheetHeader className="flex flex-row items-center gap-2 border-b border-panel-border px-3 py-2 space-y-0">
          <Wand2 className="h-3.5 w-3.5 text-primary" />
          <SheetTitle className="flex-1 truncate text-[13.5px] font-semibold">
            Revision Mode
            <span className="ml-1.5 text-[13px] font-normal text-muted-foreground">
              · {chapterTitle || 'chapter'}
            </span>
            {acceptedNotes.size > 0 && (
              <>
                <span className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                  <Check className="h-2.5 w-2.5" /> {acceptedNotes.size} applied
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setAcceptedNotes(new Set());
                    revisionApi.saveAccepted(projectId, chapterId, []).catch(() => {});
                    window.dispatchEvent(new CustomEvent('loom:revision-note-accepted', {
                      detail: { chapterId, count: 0 },
                    }));
                  }}
                  className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-secondary px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                  title="Clear accepted list — suggestions will reappear on the next re-scan"
                  data-testid="revision-clear-accepted-btn"
                >
                  <X className="h-2.5 w-2.5" /> Clear
                </button>
              </>
            )}
          </SheetTitle>
        </SheetHeader>
        <SheetDescription className="sr-only">
          Claude editorial review pane for the current chapter or highlighted selection.
        </SheetDescription>

        <div className="flex flex-wrap items-center gap-1.5 border-b border-panel-border bg-panel-header/40 px-2 py-1.5">
          <Button
            size="sm" variant="secondary"
            onClick={() => run({ useSelection: false })}
            disabled={loading}
            data-testid="revision-review-chapter"
            className="h-6 gap-1 text-[13px]"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Review chapter
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={() => run({ useSelection: true })}
            disabled={loading || !hasSelection}
            data-testid="revision-review-selection"
            className="h-6 gap-1 text-[13px]"
            title={hasSelection
              ? `Review the highlighted ${selectionText.length}-char selection`
              : 'Highlight some text in the manuscript to enable'}
          >
            <Wand2 className="h-3 w-3" />
            Review selection
          </Button>
          {hasSelection && (
            <button
              className="text-[13px] underline text-muted-foreground hover:text-foreground"
              onClick={() => setSelectionText('')}
            >
              clear ({selectionText.length}c)
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading && !result && (
            <div className="flex items-center gap-2 text-[13.5px] text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Claude is reading the chapter…
            </div>
          )}
          {error && !loading && (
            <div className="rounded-sm border border-rose-500/40 bg-rose-500/10 p-2 text-[13.5px] text-rose-700 dark:text-rose-300"
                 data-testid="revision-panel-error">
              {error}
            </div>
          )}
          {result && (
            <Tabs defaultValue="structured" className="flex h-full flex-col">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="structured" data-testid="revision-tab-structured">
                  <ListChecks className="mr-1 h-3 w-3" /> Structured
                </TabsTrigger>
                <TabsTrigger value="freeform" data-testid="revision-tab-freeform">
                  <MessageSquareText className="mr-1 h-3 w-3" /> Freeform
                </TabsTrigger>
              </TabsList>

              <TabsContent value="structured" className="mt-2 space-y-3">
                {!hasStructured && (
                  <div className="text-[13.5px] italic text-muted-foreground">
                    No structured issues surfaced.
                  </div>
                )}
                {CATEGORY_META.map((cat) => {
                  const items = struct[cat.key] || [];
                  if (!items.length) return null;
                  return (
                    <div key={cat.key} data-testid={`revision-cat-${cat.key}`}>
                      <div className="mb-1 text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {cat.label}
                      </div>
                      <ul className="space-y-1.5 pl-1 text-[13.5px] leading-snug">
                        {items.map((it, i) => {
                          const isAccepted = acceptedNotes.has(it);
                          return (
                          <li key={`${cat.key}-${i}-${String(it).slice(0, 24)}`}
                              className={cn(
                                'group flex items-start gap-1.5 rounded-sm px-1 py-0.5 transition-colors',
                                isAccepted && 'opacity-50',
                              )}
                              data-testid={`revision-note-${cat.key}-${i}`}>
                            {isAccepted
                              ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                              : <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/60" />
                            }
                            <span className={cn('flex-1', isAccepted && 'line-through decoration-emerald-600/60')}>
                              {it}
                            </span>
                            {!isAccepted && (
                              <button
                                type="button"
                                onClick={() => setApplying({
                                  note: it, category: cat.key,
                                  rewrite: '', saving: false, streaming: false,
                                })}
                                data-testid={`revision-apply-${cat.key}-${i}`}
                                className="mt-0.5 flex items-center gap-1 rounded-sm border border-transparent px-1.5 py-0.5 text-[13px] text-primary opacity-0 hover:border-primary/40 hover:bg-primary/5 group-hover:opacity-100 focus:opacity-100"
                                title="Turn this note into an editable rewrite"
                              >
                                <PenLine className="h-2.5 w-2.5" /> Apply
                              </button>
                            )}
                          </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </TabsContent>

              <TabsContent value="freeform" className="mt-2">
                {(() => {
                  const freeformAccepted = result.freeform && acceptedNotes.has(result.freeform);
                  return (
                    <>
                      <div
                        className={cn(
                          'whitespace-pre-wrap rounded-sm border border-panel-border bg-background p-2 text-[13.5px] leading-relaxed transition-opacity',
                          freeformAccepted && 'opacity-50',
                        )}
                        data-testid="revision-freeform-text"
                      >
                        {result.freeform || <em className="text-muted-foreground">No freeform note.</em>}
                      </div>
                      {result.freeform && (
                        <div className="mt-2 flex items-center justify-end gap-2">
                          {freeformAccepted ? (
                            <span
                              className="flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-[12.5px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                              data-testid="revision-freeform-accepted"
                            >
                              <Check className="h-3 w-3" /> Applied
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setApplying({
                                note: result.freeform, category: 'freeform',
                                rewrite: result.freeform, saving: false, streaming: false,
                              })}
                              data-testid="revision-apply-freeform"
                              className="flex items-center gap-1 rounded-sm border border-primary/40 bg-primary/5 px-2 py-0.5 text-[13px] text-primary hover:bg-primary/10"
                            >
                              <PenLine className="h-3 w-3" /> Turn into editable rewrite
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </SheetContent>
      {applying && (
        <ApplySuggestionDialog
          projectId={projectId}
          chapterId={chapterId}
          note={applying.note}
          initialRewrite={applying.rewrite}
          selectionText={hasSelection ? selectionText : ''}
          onCancel={() => setApplying(null)}
          onApplied={async () => {
            // Mark this note as accepted — strike-through in UI and
            // excluded from future re-scans.
            const nextSet = new Set([...acceptedNotes, applying.note]);
            setAcceptedNotes(nextSet);
            // Persist so the list survives panel close/reopen.
            revisionApi.saveAccepted(projectId, chapterId, [...nextSet]).catch(() => {});
            // Notify the Chapters sidebar to update its badge count immediately.
            window.dispatchEvent(new CustomEvent('loom:revision-note-accepted', {
              detail: { chapterId, count: nextSet.size },
            }));
            setApplying(null);
            try {
              const chs = await chaptersApi.list(projectId);
              setChapters(chs.map((c) => ({
                id: c.id, number: c.number, title: c.title, words: c.words,
                status: c.status, content: c.content, sentiment: c.sentiment,
              })));
              toast.success('Rewrite applied to the chapter.');
            } catch { /* soft */ }
          }}
        />
      )}
    </Sheet>
  );
}
